/** @jsxImportSource react */
import { useState } from 'react'
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography,
} from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { format } from 'date-fns'
import { instagramAPI } from '../../api/crm'
import { useTenantKey } from '../../hooks/useCrmQueries'
import { getErrorMessage } from '../../utils/apiErrors'
import { useNotify } from '../common/NotificationProvider'

const MARKS = { SENT: '✓', READ: '✓ seen', FAILED: 'not sent' }

/** A record's Instagram DMs, and a reply box (Instagram allows replies only within 24 hours of their last message). */
const InstagramDialog = ({ record, onClose }) => {
  const tenantId = useTenantKey()
  const queryClient = useQueryClient()
  const notify = useNotify()
  const key = [tenantId, 'instagram', 'thread', record.id]
  const thread = useQuery({ queryKey: key, queryFn: () => instagramAPI.thread(record.id), refetchInterval: 15_000 })
  const [text, setText] = useState('')
  const send = useMutation({
    mutationFn: () => instagramAPI.send(record.id, { text: text.trim() }),
    onSuccess: (m) => {
      queryClient.invalidateQueries({ queryKey: key })
      queryClient.invalidateQueries({ queryKey: [tenantId, 'records', 'activity'] })
      if (m.status === 'FAILED') notify(`Not sent: ${m.error}`, 'error')
      setText('')
    },
  })
  const th = thread.data
  const messages = [...(th?.messages || [])].reverse()
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Instagram{th?.handle ? ` · @${th.handle}` : ''}</DialogTitle>
      <DialogContent dividers sx={{ bgcolor: 'background.default', minHeight: 240, maxHeight: 420 }}>
        {thread.error && <Alert severity="error">{getErrorMessage(thread.error)}</Alert>}
        {th && !th.igsid && (
          <Alert severity="info">
            No messages yet. Instagram only lets a business reply, so their DM to your account comes first; it appears here
            {th.handle ? '' : ' once the record has their Instagram handle or an admin links the message'}.
          </Alert>
        )}
        <Stack spacing={1}>
          {messages.map((m) => (
            <Box key={m.id} sx={{ alignSelf: m.direction === 'OUT' ? 'flex-end' : 'flex-start', maxWidth: '80%' }}>
              <Box sx={{ px: 1.5, py: 1, borderRadius: 2, bgcolor: m.direction === 'OUT' ? 'primary.main' : 'background.paper',
                color: m.direction === 'OUT' ? 'primary.contrastText' : 'text.primary', border: m.direction === 'OUT' ? 0 : 1, borderColor: 'divider' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{m.body}</Typography>
              </Box>
              <Typography variant="caption" color={m.status === 'FAILED' ? 'error' : 'text.secondary'} sx={{ display: 'block', textAlign: m.direction === 'OUT' ? 'right' : 'left' }}>
                {format(new Date(m.createdOn), 'd MMM HH:mm')}
                {m.direction === 'OUT' ? ` · ${MARKS[m.status] || ''}${m.sentBy === 'Instagram app' ? ' · from the Instagram app' : ''}` : ''}
                {m.status === 'FAILED' && m.error ? ` · ${m.error}` : ''}
              </Typography>
            </Box>
          ))}
        </Stack>
      </DialogContent>
      {th?.igsid && (
        <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', gap: 1, p: 2 }}>
          {send.error && <Alert severity="error">{getErrorMessage(send.error)}</Alert>}
          {th.windowOpen ? (
            <TextField multiline minRows={2} maxRows={6} placeholder="Message" value={text} onChange={(e) => setText(e.target.value)} inputProps={{ maxLength: 1000 }}
              helperText={`You can reply until ${format(new Date(th.windowClosesOn), 'd MMM HH:mm')}`} />
          ) : (
            <Typography variant="caption" color="text.secondary">They last wrote more than 24 hours ago: Instagram allows a reply once they write again.</Typography>
          )}
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button onClick={onClose}>Close</Button>
            {th.windowOpen && <Button variant="contained" disabled={send.isPending || !text.trim()} onClick={() => send.mutate()}>Send</Button>}
          </Stack>
        </DialogActions>
      )}
    </Dialog>
  )
}

export default InstagramDialog
